import { VelocityClient, OracleSource } from '@velocity-exchange/sdk';
import {
	ENUM_UTILS,
	MarketId,
	UIMarket,
	getOracleCrankIxs as getOracleCrankIxsShared,
	OracleCrankDataFetcher,
	isPythOracle,
	trimTrailingZeros,
} from '@velocity-exchange/common';
import { TransactionInstruction } from '@solana/web3.js';
import { WalletName } from '@solana/wallet-adapter-base';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { WALLET_NAME_SQUADS_X } from 'src/constants/wallets';
import NumLib from './NumLib';
import ExchangeHistoryClient from './exchangeHistoryClient';

export const getOracleUrl = (
	oracleSource: OracleSource,
	symbol: string,
	oraclePubKey: string,
	_isDevnet = false
): string => {
	if (isPythOracle(oracleSource)) {
		return `https://insights.pyth.network/price-feeds/Crypto.${symbol.toUpperCase()}%2FUSD`;
	} else if (ENUM_UTILS.match(oracleSource, OracleSource.SWITCHBOARD)) {
		return `https://app.switchboard.xyz/solana/mainnet/feed/${oraclePubKey}`;
	} else if (
		ENUM_UTILS.match(oracleSource, OracleSource.SWITCHBOARD_ON_DEMAND)
	) {
		return `https://ondemand.switchboard.xyz/solana/mainnet/feed/${oraclePubKey}`;
	} else {
		return '';
	}
};

export const getOracleOffsetLabel = (
	offset: number,
	excludePrefix?: boolean
) => {
	return `${!excludePrefix ? 'Oracle Price ' : ''}${
		offset < 0 ? '-' : '+'
	}$${trimTrailingZeros(NumLib.formatNum.toDisplayPrice(Math.abs(offset)), 2)}`;
};

const webCrankDataFetcher: OracleCrankDataFetcher = async (source, feedIds) => {
	const result = await ExchangeHistoryClient.getPythCranks(
		source,
		feedIds as string[] | number[]
	);
	return {
		success: result.success,
		data: result.body?.data,
	};
};

export const getOracleCrankIxs = async (
	marketIds: MarketId[],
	driftClient: VelocityClient,
	currentWalletName: WalletName<string>,
	slot: { current: number; customStalenessThreshold?: number },
	maxCountPythPull = 1,
	preceedingIxsCount = 2
): Promise<TransactionInstruction[]> => {
	if (Env.disablePervasiveOracleCrank) return [];

	if (currentWalletName === WALLET_NAME_SQUADS_X) return [];

	const stalenessThreshold =
		slot.customStalenessThreshold ?? Env.oracleCrankStalenessThreshold;

	const filteredConfigs = marketIds
		.map((id) => {
			const oracleData = id.isPerp
				? driftClient.getOracleDataForPerpMarket(id.marketIndex)
				: driftClient.getOracleDataForSpotMarket(id.marketIndex);
			return {
				marketId: id,
				slotLastUpdated: oracleData?.slot?.toNumber() ?? 0,
			};
		})
		.filter((idAndSlot) => {
			return idAndSlot.slotLastUpdated < slot.current - stalenessThreshold;
		})
		.sort((a, b) => a.slotLastUpdated - b.slotLastUpdated)
		.map((idAndSlot) => {
			return idAndSlot.marketId.isPerp
				? UIMarket.perpMarkets[idAndSlot.marketId.marketIndex]
				: UIMarket.spotMarkets[idAndSlot.marketId.marketIndex];
		});

	return getOracleCrankIxsShared({
		marketConfigs: filteredConfigs,
		velocityClient: driftClient,
		fetchCrankData: webCrankDataFetcher,
		maxPythPullCranks: maxCountPythPull,
		precedingIxsCount: preceedingIxsCount,
	});
};
